import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import './login.css';

// Import Firebase configuration and authentication
import { auth, firebaseAuth, firebaseGetMultiFactorResolver } from '../../../js/external-js/config/main_config';
import firebase from 'firebase/compat/app';
import { useNavigate } from 'react-router-dom';
import { getAvailableFeatures } from '@/utils/featuresUtil';

const Login = () => {
  const features = getAvailableFeatures();
  const isSelfServiceEnabled = features.find((feature) => feature.name === 'Self-Service');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSignUp, setIsLoadingSignUp] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  // State for additional UI components
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resolver, setResolver] = useState<any>(null);

  // Handle email/password authentication
  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setShowError(false);

    if (email.length > 1 && password.length >= 8) {
      setIsLoading(true);

      try {
        await auth.signInWithEmailAndPassword(email, password);
        // If successful, auth state change will redirect
      } catch (error: any) {
        console.log('Error:', error);

        if (error.code === 'auth/multi-factor-auth-required') {
          const mfaResolver = firebaseGetMultiFactorResolver(auth, error);
          setResolver(mfaResolver);
          setShowMFA(true);
        } else {
          setErrorMessage(error.message || 'Invalid email or password');
          setShowError(true);
          setIsLoading(false);
        }
      }
    } else {
      setErrorMessage('Please enter a valid email and password (min 8 characters)');
      setShowError(true);
      setIsLoading(false);
    }
  }

  // Handle Google sign in
  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const provider = new firebaseAuth.GoogleAuthProvider();
      await auth.signInWithPopup(provider);
      // If successful, auth state change will redirect
    } catch (error: any) {
      console.log(error);

      if (error.code === 'auth/multi-factor-auth-required') {
        const mfaResolver = firebaseGetMultiFactorResolver(auth, error);
        setResolver(mfaResolver);
        setShowMFA(true);
      } else {
        setErrorMessage(error.message || 'Google sign in failed');
        setShowError(true);
        setIsLoading(false);
      }
    }
  };

  // Handle Microsoft sign in
  const handleMicrosoftSignIn = async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const provider = new firebaseAuth.OAuthProvider('microsoft.com');
      await auth.signInWithPopup(provider);
      // If successful, auth state change will redirect
    } catch (error: any) {
      console.log(error);

      if (error.code === 'auth/multi-factor-auth-required') {
        const mfaResolver = firebaseGetMultiFactorResolver(auth, error);
        setResolver(mfaResolver);
        setShowMFA(true);
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        // Handle account linking case
        setErrorMessage('This email is already associated with another sign-in method');
        setShowError(true);
      } else {
        setErrorMessage(error.message || 'Microsoft sign in failed');
        setShowError(true);
      }
      setIsLoading(false);
    }
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setErrorMessage('Please enter your email address');
      setShowError(true);
      return;
    }

    setIsLoading(true);
    setShowError(false);

    try {
      await auth.sendPasswordResetEmail(resetEmail);
      setResetEmailSent(true);
      setTimeout(() => {
        setShowPasswordReset(false);
        setResetEmailSent(false);
        setResetEmail('');
      }, 5000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to send reset email');
      setShowError(true);
    }
    setIsLoading(false);
  };

  // State for MFA verification
  const [verificationId, setVerificationId] = useState<string>('');
  const [phoneInfoOptions, setPhoneInfoOptions] = useState<any>(null);
  const [smsAppVerifier, setSmsAppVerifier] = useState<any>(null);

  // Auto-submit OTP when 6 digits are entered
  useEffect(() => {
    if (showMFA && verificationCode.length === 6 && !isLoading) {
      handleVerifyCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode, showMFA, isLoading]);

  // Generate reCAPTCHA verifier for MFA
  const generateRecaptcha = async () => {
    try {
      if (!smsAppVerifier) {
        const recaptchaVerifier = new firebaseAuth.RecaptchaVerifier(
          'recaptcha-container',
          {
            size: 'invisible',
            callback: () => {
              console.log('reCAPTCHA solved!');
            },
            'error-callback': (error: any) => {
              console.error('reCAPTCHA error:', error);
              setErrorMessage('Failed to verify reCAPTCHA. Please try again.');
              setShowError(true);
            }
          },
          auth.app
        );

        await recaptchaVerifier.render();
        setSmsAppVerifier(recaptchaVerifier);
        return recaptchaVerifier;
      }
      return smsAppVerifier;
    } catch (error: any) {
      console.error('Error generating reCAPTCHA:', error);
      setErrorMessage('Failed to initialize verification. Please try again.');
      setShowError(true);
      setIsLoading(false);
      return null;
    }
  };

  // Send verification code when MFA is shown
  useEffect(() => {
    if (showMFA && resolver) {
      const sendVerificationCode = async () => {
        try {
          const appVerifier = await generateRecaptcha();
          if (!appVerifier) return;

          // Get the first phone hint (we could add UI to select from multiple hints if needed)
          const hint = resolver.hints[0];

          // Setup phone info options for verification
          const options = {
            multiFactorHint: hint,
            session: resolver.session
          };
          setPhoneInfoOptions(options);

          // Create phone auth provider and verify phone number
          const phoneAuthProvider = new firebaseAuth.PhoneAuthProvider(auth);
          const verificationId = await phoneAuthProvider.verifyPhoneNumber(options, appVerifier);
          setVerificationId(verificationId);

          // Display last 4 digits of the phone number
          const phoneNumber = hint.phoneNumber;
          const lastFourDigits = phoneNumber.slice(-4);
          console.log(`Verification code sent to number ending in ${lastFourDigits}`);

          // Reset loading state after verification code is sent
          setIsLoading(false);
        } catch (error: any) {
          console.error('Error sending verification code:', error);
          setErrorMessage('Failed to send verification code. Please try again.');
          setShowError(true);
          setIsLoading(false);
        }
      };

      sendVerificationCode();
    }
  }, [showMFA, resolver]);

  // Handle MFA verification
  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setErrorMessage('Please enter a valid 6-digit verification code');
      setShowError(true);
      return;
    }

    setIsLoading(true);
    setShowError(false);

    try {
      if (!verificationId || !resolver) {
        throw new Error('Verification session expired. Please try signing in again.');
      }

      // Create credential with verification ID and code
      const credential = firebaseAuth.PhoneAuthProvider.credential(verificationId, verificationCode);

      // Create multi-factor assertion
      const multiFactorAssertion = firebaseAuth.PhoneMultiFactorGenerator.assertion(credential);

      console.log('Multi-factor assertion:', multiFactorAssertion);

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('MFA sign-in timed out')), 30000);
      });

      try {
        // Resolve sign in with the assertion with timeout
        await Promise.race([resolver.resolveSignIn(multiFactorAssertion), timeoutPromise]);
        console.log('MFA sign-in successful!');

        // Force redirect if auth state change doesn't trigger
        setTimeout(() => {
          const user = auth.currentUser;
          if (user) {
            // Handle redirect manually if needed
            const urlParams = new URLSearchParams(window.location.search);
            let returnUrl = urlParams.get('returnUrl');

            if (returnUrl === '%2F') {
              returnUrl = null;
            }

            if (returnUrl) {
              window.location.href = returnUrl.replace('%2F', '/');
            } else {
              window.location.href = '/';
            }
          }
        }, 2000);
      } catch (timeoutError) {
        console.error('MFA resolution timed out:', timeoutError);
        throw new Error('MFA verification timed out. Please try again.');
      }

      // Auth state change listener will handle redirect after successful sign-in
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setErrorMessage(error.message || 'Invalid verification code');
      setShowError(true);
      setIsLoading(false);

      // Clear the verification code on error
      setVerificationCode('');

      // Reset MFA flow on error
      if (error.message?.includes('timed out') || error.code === 'auth/timeout') {
        // Reset the MFA flow completely
        setShowMFA(false);
        if (smsAppVerifier) {
          smsAppVerifier.clear();
          setSmsAppVerifier(null);
        }
        setResolver(null);
        setVerificationId('');
      }
      // If reCAPTCHA verification failed, try to regenerate it
      else if (error.code === 'auth/invalid-recaptcha-token') {
        if (smsAppVerifier) {
          smsAppVerifier.clear();
          setSmsAppVerifier(null);
        }
        await generateRecaptcha();
      }
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const token = await user.getIdToken();
        const uid = user.uid;
        // Set cookie with SameSite=None and Secure, and DO NOT set Domain so each subdomain gets a unique cookie
        document.cookie = `__session_react=${token}--__--${uid}; Path=/; SameSite=None; Secure`;


        // Check user role in Firestore before redirecting
        const db = firebase.firestore();
        try {
          const userDoc = await db.collection('users').doc(uid).get();

          if (userDoc.exists && userDoc.data().roles) {
            const userData = userDoc.data();

            // Handle role-based redirection
            if (userData.roles.user === true) {
              // Check if we're in a development/special environment
              const hostname = window.location.hostname;
              const isDevelopmentEnv =
                hostname.includes('localhost') ||
                hostname.includes('127.0.0.1') ||
                hostname.endsWith('.local') ||
                hostname.includes('deiterate-hybrid.web.app');

              // Use app.deit.dev for development environments, otherwise use production URL format
              if (isDevelopmentEnv) {
                window.location.href = 'https://app.deit.dev';
              } else {
                // For production: construct app subdomain URL from the current hostname
                const domainParts = hostname.split('.');
                if (domainParts.length >= 2) {
                  // Ensures we have at least domain.tld
                  window.location.href = `https://app.${domainParts[domainParts.length - 2]}.${domainParts[domainParts.length - 1]}`;
                } else {
                  // Fallback in case hostname parsing fails
                  window.location.href = 'https://app.deit.dev';
                }
              }
              return;
            }
          }

          // If no special role or document doesn't exist, use default redirect logic
          const urlParams = new URLSearchParams(window.location.search);
          let returnUrl = urlParams.get('returnUrl');

          if (returnUrl === '%2Findex_vanilla.html' || returnUrl === '%2Fdashboard') {
            returnUrl = null;
          }

          if (returnUrl) {
            window.location.href = returnUrl.replace('%2F', '/');
          } else {
            // window.location.href = 'index_vanilla.html';
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error checking user role:', error);
          // Fall back to default redirect if there's an error
          // window.location.href = 'index_vanilla.html';
          navigate('/dashboard');
        }
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="auth-container">
      {/* Hidden reCAPTCHA container for MFA */}
      <div id="recaptcha-container" style={{ display: 'none' }}></div>
      {/* Desktop view - two column layout */}
      <div className="desktop-view">
        {/* Left column - dark background with logo and testimonial */}
        <div className="left-column">
          <div className="dark-overlay"></div>
          <div className="logo-container">
            <img src="/assets/img/large_logo_white.svg" alt="de.iterate Logo" className="auth-logo" />
            {/* <span>de.iterate</span> */}
          </div>
          <div className="testimonial">
            <blockquote>
              <p>&ldquo;de.cide with clarity, de.liver with confidence, and de.iterate with ease.&rdquo;</p>
              <footer>Harold Quackmore</footer>
            </blockquote>
          </div>
        </div>

        {/* Right column - login form */}
        <div className="right-column">
          <div className="auth-form-container">
            {!showMFA ? (
              <div className="auth-header">
                <h1>Sign in to your account</h1>
                <p>Enter your email below to sign in to your account</p>
              </div>
            ) : (
              <div className="auth-header">
                <h1>Two-factor Authentication</h1>
                <p>Enter the verification code sent to your phone</p>
              </div>
            )}

            {/* Auth Form */}
            <div className="auth-form">
              {showPasswordReset ? (
                <div className="password-reset-form">
                  <h2>Reset your password</h2>
                  <p>Enter your email address and we'll send you a link to reset your password.</p>
                  {showError && <div className="error-message">{errorMessage}</div>}
                  <div className="form-group">
                    <Input
                      id="reset-email"
                      placeholder="name@example.com"
                      type="email"
                      autoCapitalize="none"
                      autoComplete="email"
                      autoCorrect="off"
                      disabled={isLoading || resetEmailSent}
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="mb-4"
                    />
                  </div>
                  <div className="form-actions">
                    <Button className="reset-button" disabled={isLoading || resetEmailSent} onClick={handlePasswordReset}>
                      {isLoading && (
                        <svg
                          className="spinner-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                      )}
                      {resetEmailSent ? 'Email Sent' : 'Send Reset Link'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setShowError(false);
                      }}
                      disabled={isLoading}>
                      Back to Login
                    </Button>
                  </div>
                </div>
              ) : showMFA ? (
                <div className="mfa-form">
                  {showError && <div className="error-message">{errorMessage}</div>}
                  <div className="form-group">
                    <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode} disabled={isLoading} className="mb-4">
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button className="verify-button" disabled={isLoading} onClick={handleVerifyCode}>
                    {isLoading && (
                      <svg
                        className="spinner-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    )}
                    Verify
                  </Button>
                </div>
              ) : (
                <>
                  <form onSubmit={(e) => {
                    if (!isLoading) {
                      onSubmit(e);
                    } else {
                      e.preventDefault();
                    }
                  }}>
                    {showError && <div className="error-message">{errorMessage}</div>}
                    <div className="form-group">
                      <Input
                        id="email"
                        placeholder="name@example.com"
                        type="email"
                        autoCapitalize="none"
                        autoComplete="email"
                        autoCorrect="off"
                        disabled={isLoading}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mb-4"
                      />
                    </div>
                    <div className="form-group">
                      <Input
                        id="password"
                        placeholder="Password"
                        type="password"
                        autoCapitalize="none"
                        autoComplete="current-password"
                        autoCorrect="off"
                        disabled={isLoading}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mb-4"
                      />
                      <div className="forgot-password">
                        <button
                          type="button"
                          className="forgot-password-link"
                          onClick={() => {
                            setShowPasswordReset(true);
                            setResetEmail(email);
                            setShowError(false);
                          }}>
                          Forgot password?
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="sign-in-button" disabled={isLoading}>
                      {isLoading && (
                        <svg
                          className="spinner-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                      )}
                      Sign In with Email
                    </Button>
                    {/* Sign up button */}
                    {isSelfServiceEnabled ? (
                      <Button type="button" variant="outline" className="w-full mt-4" disabled={isLoadingSignUp || isLoading} onClick={() => {
                        // TODO:Navigati to onboarding page
                        navigate('/signup');
                      }}>
                        {isLoadingSignUp && (
                          <svg
                            className="spinner-icon"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                          </svg>
                        )}
                        Sign Up
                      </Button>
                    ) : null}
                  </form>
                  <div className="divider">
                    <span>Or continue with</span>
                  </div>
                  <div className="social-buttons">
                    <Button variant="outline" disabled={isLoading} className="social-button" onClick={handleGoogleSignIn}>
                      <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3276 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z"
                          fill="#34A853"
                        />
                        <path d="M5.50253 14.3003C4.99987 12.8099 4.99987 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC04" />
                        <path
                          d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.034466 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z"
                          fill="#EA4335"
                        />
                      </svg>
                      Google
                    </Button>
                    <Button variant="outline" disabled={isLoading} className="social-button" onClick={handleMicrosoftSignIn}>
                      <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                        <path d="M0 0H10V10H0V0Z" fill="#F25022" />
                        <path d="M11 0H21V10H11V0Z" fill="#7FBA00" />
                        <path d="M0 11H10V21H0V11Z" fill="#00A4EF" />
                        <path d="M11 11H21V21H11V11Z" fill="#FFB900" />
                      </svg>
                      Microsoft
                    </Button>
                  </div>
                </>
              )}
            </div>

            <p className="terms-text">
              By clicking continue, you agree to our{' '}
              <a href="https://deiterate.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="terms-link">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="https://deiterate.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="terms-link">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      {/* Mobile view */}
      <div className="mobile-view">
        <img src="/assets/img/logo.png" alt="de.iterate Logo" className="auth-logo mobile-logo" />
        <div className="mobile-form-container">
          {!showMFA ? (
            <div className="auth-header">
              <h1>Sign in to your account</h1>
              <p>Enter your email below to sign in to your account</p>
            </div>
          ) : (
            <div className="auth-header">
              <h1>Two-factor Authentication</h1>
              <p>Enter the verification code sent to your phone</p>
            </div>
          )}

          {showPasswordReset ? (
            <div className="password-reset-form">
              <h2>Reset your password</h2>
              <p>Enter your email address and we'll send you a link to reset your password.</p>
              {showError && <div className="error-message">{errorMessage}</div>}
              <div className="form-group">
                <Input
                  id="reset-email-mobile"
                  placeholder="name@example.com"
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isLoading || resetEmailSent}
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="mb-4"
                />
              </div>
              <div className="form-actions">
                <Button className="reset-button" disabled={isLoading || resetEmailSent} onClick={handlePasswordReset}>
                  {isLoading && (
                    <svg
                      className="spinner-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  )}
                  {resetEmailSent ? 'Email Sent' : 'Send Reset Link'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordReset(false);
                    setShowError(false);
                  }}
                  disabled={isLoading}>
                  Back to Login
                </Button>
              </div>
            </div>
          ) : showMFA ? (
            <div className="mfa-form">
              {showError && <div className="error-message">{errorMessage}</div>}
              <div className="form-group">
                <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode} disabled={isLoading} className="mb-4">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button className="verify-button" disabled={isLoading} onClick={handleVerifyCode}>
                {isLoading && (
                  <svg
                    className="spinner-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                )}
                Verify
              </Button>
            </div>
          ) : (
            <>
              <div onSubmit={onSubmit} className="mobile-form">
                {showError && <div className="error-message">{errorMessage}</div>}
                <Input
                  id="email-mobile"
                  placeholder="name@example.com"
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isLoading}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mb-4"
                />
                <Input
                  id="password-mobile"
                  placeholder="Password"
                  type="password"
                  autoCapitalize="none"
                  autoComplete="current-password"
                  autoCorrect="off"
                  disabled={isLoading}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mb-4"
                />
                <div className="forgot-password">
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={() => {
                      setShowPasswordReset(true);
                      setResetEmail(email);
                      setShowError(false);
                    }}>
                    Forgot password?
                  </button>
                </div>
                <Button className="mobile-sign-in-button" disabled={isLoading} onClick={onSubmit}>
                  {isLoading && (
                    <svg
                      className="spinner-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  )}
                  Sign In with Email
                </Button>
              </div>

              <div className="divider">
                <span>Or continue with</span>
              </div>

              <div className="mobile-social-buttons">
                <Button variant="outline" className="mobile-social-button" disabled={isLoading} onClick={handleGoogleSignIn}>
                  <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3276 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z"
                      fill="#34A853"
                    />
                    <path d="M5.50253 14.3003C4.99987 12.8099 4.99987 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC04" />
                    <path
                      d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.034466 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z"
                      fill="#EA4335"
                    />
                  </svg>
                  Google
                </Button>
                <Button variant="outline" className="mobile-social-button" disabled={isLoading} onClick={handleMicrosoftSignIn}>
                  <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                    <path d="M0 0H10V10H0V0Z" fill="#F25022" />
                    <path d="M11 0H21V10H11V0Z" fill="#7FBA00" />
                    <path d="M0 11H10V21H0V11Z" fill="#00A4EF" />
                    <path d="M11 11H21V21H11V11Z" fill="#FFB900" />
                  </svg>
                  Microsoft
                </Button>
              </div>
            </>
          )}

          <p className="mobile-terms-text">
            By clicking continue, you agree to our{' '}
            <a href="/terms" className="terms-link">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="terms-link">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
